import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { ProjectStatus } from "../constants";
import { PosItemDisable } from "./posConfigService";
import { PosProjectRow, PosConfigLevel } from "./constants";

const FRANCHISE_SCOPE_SHOP_IDS: ReadonlySet<string> = new Set(["", "0"]);

function isFranchiseScopeEntry(entry: PosItemDisable): boolean {
    const shopId = entry.shopId;
    if (shopId === null || shopId === undefined) return true;
    return FRANCHISE_SCOPE_SHOP_IDS.has(shopId);
}

function isShopScopeId(value: unknown): value is string {
    return (
        typeof value === "string" &&
        value.length > 0 &&
        !FRANCHISE_SCOPE_SHOP_IDS.has(value)
    );
}

function resolveScopedShopId(
    selectedRow: PosProjectRow | null,
    configLevel: PosConfigLevel,
    stateShopId: string
): string | null {
    if (configLevel === "franchise") return null;
    // Filter out franchise-scope sentinels ("", "0") at each fallback step:
    // a row whose id_boutique/shopId is "0" must NOT collapse the matching
    // to franchise scope — keep falling through until we find a real shop id.
    // Bind to locals first so TS narrows them via the type-guard (narrowing
    // doesn't propagate through optional chaining on the parameter directly).
    const idBoutique = selectedRow?.id_boutique;
    if (isShopScopeId(idBoutique)) return idBoutique;
    const rowShopId = selectedRow?.shopId;
    if (isShopScopeId(rowShopId)) return rowShopId;
    // Shop sub-rows in the franchise list carry their apps in `subRows`;
    // each app row has `shopId` set to the parent boutique. When the shop
    // row itself lacks a usable id, borrow one from the first sub-row that
    // exposes a real shop id.
    const subRows = selectedRow?.subRows;
    if (Array.isArray(subRows)) {
        for (const sub of subRows) {
            const subShopId = (sub as { shopId?: unknown })?.shopId;
            if (isShopScopeId(subShopId)) return subShopId;
        }
    }
    // Application rows sometimes carry only `appId` without an explicit
    // parent shop reference. Fall back to the shopId seeded by the modal
    // opener (set in posConfigSlice at openPosConfigModal.pending) so that
    // apps under a boutique still resolve to that boutique's scope —
    // otherwise a boutique-scope deactivation wouldn't cascade to its apps.
    if (isShopScopeId(stateShopId)) return stateShopId;
    return null;
}

function resolveRowStatus(row: PosProjectRow | null): string | undefined {
    if (!row) return undefined;
    const statutFr = row.Statut;
    if (typeof statutFr === "string") return statutFr;
    const statusEn = row.Status;
    return typeof statusEn === "string" ? statusEn : undefined;
}

/**
 * Hook returning a stable predicate `isItemDisabledInGeneral(itemId)` that
 * reflects whether the given template item has been deactivated in the
 * general settings for the relevant scope (franchise / shop / application).
 *
 * Greying is suppressed when the selected row is a published template
 * (`Statut === "Publié"`) at any level (franchise or shop): published
 * templates are frozen and must not reflect later general-settings
 * deactivations. `isConsultation` (oneShop / read-only entry points) is
 * also honored so consultation views stay clean.
 *
 * Scope rules for matching `itemsDisable` entries:
 *  - Franchise level: only franchise-scope entries (shopId null/empty/"0")
 *    apply. A deactivation flagged for a specific shop must NOT bubble up
 *    to the franchise template view.
 *  - Shop / application level: franchise-scope entries apply (a franchise-
 *    wide deactivation cascades down) AND entries whose shopId matches the
 *    current shop scope apply (boutique-specific deactivation reflects on
 *    its template and the applications inside it).
 */
export function useIsItemDisabledInGeneral(): (itemId: string) => boolean {
    const itemsDisable = useSelector(
        (state: RootState) => state.posConfig.itemsDisable
    );
    const selectedRow = useSelector(
        (state: RootState) => state.posConfig.selectedRow
    );
    const configLevel = useSelector(
        (state: RootState) => state.posConfig.configLevel
    );
    const isConsultation = useSelector(
        (state: RootState) => state.posConfig.isConsultation
    );
    const stateShopId = useSelector(
        (state: RootState) => state.posConfig.shopId
    );

    const scopedShopId = React.useMemo(
        () => resolveScopedShopId(selectedRow, configLevel, stateShopId),
        [selectedRow, configLevel, stateShopId]
    );

    const isPublishedRow = React.useMemo(
        () => resolveRowStatus(selectedRow) === ProjectStatus.PUBLISHED,
        [selectedRow]
    );

    const disabledIds = React.useMemo(() => {
        if (isConsultation || isPublishedRow) return null;
        const set = new Set<string>();
        for (const d of itemsDisable) {
            const isFranchiseScope = isFranchiseScopeEntry(d);
            if (scopedShopId === null) {
                if (isFranchiseScope) set.add(d.id);
            } else if (isFranchiseScope || d.shopId === scopedShopId) {
                set.add(d.id);
            }
        }
        return set;
    }, [itemsDisable, scopedShopId, isConsultation, isPublishedRow]);

    return React.useCallback(
        (itemId: string) => (disabledIds ? disabledIds.has(itemId) : false),
        [disabledIds]
    );
}
